// Revise ▸ Readers (the Mac's BetaReadersSheet, for web links): rounds of private links to one checkpoint, one per
// reader. A new round takes a checkpoint (or makes one now), readers by name, a notes-by date and a message; each link
// can be copied or shown as a QR code, and withdrawn. Each reader's status (opened, how far they've read if they chose
// to share it, notes) comes from the relay and the project. Readers' notes are placed as comments when the project
// opens, and here on demand; they show in Revise ▸ Comments.
import { errorText } from '@writing-suite/client/browser/usage.ts';
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import qrcode from 'qrcode-generator';
import type { OpenedProject } from '@writing-suite/core';
import type { WebReader, WebReaderRound } from '@writing-suite/core/review';
import type { PageProps } from '../shell/ProjectShell.tsx';
import { announce, useApp } from '../shell/context.ts';
import { navigate } from '../shell/route.ts';
import { ReaderLinks, type LinkStatus } from '../store/readers.ts';
import { authorName } from '../store/useProject.ts';
import { ConfirmDialog } from './Menu.tsx';
import { useWorkspace } from '../shell/workspace.ts';

const DAY = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
const WHEN = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

/** A date input's value (local midnight) as a date, or null. */
const fromDateInput = (v: string) => (v ? new Date(`${v}T00:00:00`) : null);

export function ReadersPage({ project, live, hrefFor }: PageProps) {
  const app = useApp();
  // Reader links are the owner's (W9a): a co-writer or commenter sees the rounds, not the links' controls.
  const ws = useWorkspace();
  const isOwner = (ws?.role ?? 'owner') === 'owner';
  const links = useMemo(() => (live ? new ReaderLinks(app.account.session.api, live) : null), [live, app.account.session.api]);
  // Rounds and kept progress come from the project document (rebuilt with it).
  const rounds = useMemo(() => links?.rounds() ?? [], [links, project]);
  const progress = useMemo(() => links?.progress() ?? new Map<string, number>(), [links, project]);
  const [status, setStatus] = useState<{ readOrigin: string | null; links: Map<string, LinkStatus> } | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [placing, setPlacing] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<{ round: WebReaderRound; reader: WebReader } | null>(null);

  const refresh = useCallback(async () => {
    if (!links || !app.online || !isOwner) return;
    try {
      setStatus(await links.status());
      setStatusError(null);
    } catch (e) {
      setStatusError(e instanceof Error ? e.message : String(e));
    }
  }, [links, app.online, isOwner]);
  useEffect(() => void refresh(), [refresh]);

  const place = async () => {
    if (!links) return;
    setPlacing('Placing notes…');
    try {
      const r = await links.place();
      const text = r.added || r.updated
        ? `${r.added} new ${r.added === 1 ? 'note' : 'notes'} placed as comments${r.updated ? `, ${r.updated} updated` : ''}.`
        : r.waiting.length
          ? `${r.waiting.length} ${r.waiting.length === 1 ? 'note is' : 'notes are'} waiting for text that hasn’t reached this computer.`
          : 'No new notes.';
      setPlacing(text);
      announce(text);
      await refresh();
    } catch (e) {
      setPlacing(`Notes couldn’t be placed: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const canWrite = !!live?.writable && app.online && isOwner;
  const readOrigin = status?.readOrigin ?? null;
  const opened = project as OpenedProject;
  const checkpoints = useMemo(() => [...(opened.record?.checkpoints ?? [])].reverse(), [opened]);

  return (
    <div className="readers-page">
      <header className="page-head">
        <h1 tabIndex={-1}>Readers</h1>
        <p className="page-sub">
          Share a checkpoint as private links, one for each reader. They read it in any browser and comment on passages; their notes come back
          here as comments. The server never sees the book or the notes.
        </p>
      </header>
      <div className="readers-actions">
        <button type="button" className="button primary" disabled={!canWrite} onClick={() => setCreating(true)}>
          New round…
        </button>
        <button type="button" className="button secondary" disabled={!canWrite || !rounds.length || placing === 'Placing notes…'} onClick={() => void place()}>
          Place new notes
        </button>
        <a className="link-button" href={hrefFor('revise/comments', null)} onClick={(e) => (e.preventDefault(), navigate(hrefFor('revise/comments', null)))}>
          See comments
        </a>
        {placing ? (
          <p className="status-note" role="status" data-placing>
            {placing}
          </p>
        ) : null}
      </div>
      {!isOwner ? (
        <p className="status-note owner-only" data-owner-only="readers">
          Only {ws?.ownerName || 'the project’s owner'} makes reader links for this project.
        </p>
      ) : null}
      {!app.online ? <p className="status-note">Offline: links and notes need a connection.</p> : null}
      {statusError ? (
        <p className="error" role="alert">
          The server couldn’t be asked about the links: {statusError}
        </p>
      ) : null}
      {creating && links ? (
        <NewRoundForm
          checkpoints={checkpoints.map((c) => ({ id: c.id, name: c.name, createdAt: c.createdAt }))}
          links={links}
          onDone={async (round) => {
            setCreating(false);
            if (round) announce(`Round “${round.name}” made with ${round.readers.length} ${round.readers.length === 1 ? 'link' : 'links'}.`);
            await refresh();
          }}
        />
      ) : null}
      {rounds.length === 0 && !creating ? (
        <div className="page-message">
          <h2>No rounds yet</h2>
          <p className="lede">A round sends one checkpoint to a few readers, each with their own link that you can withdraw at any time.</p>
        </div>
      ) : null}
      {rounds.map((round) => (
        <RoundView
          key={round.id}
          round={round}
          checkpointName={checkpoints.find((c) => c.id === round.checkpointID)?.name ?? null}
          readOrigin={readOrigin}
          status={status?.links ?? null}
          progress={progress}
          canWrite={canWrite}
          onRevoke={(reader) => setRevoking({ round, reader })}
        />
      ))}
      {revoking && links ? (
        <ConfirmDialog
          title={`Withdraw ${revoking.reader.name}’s link?`}
          message={<p>Their copy and any notes not yet placed are deleted from the server at once, and the link stops working. Notes already placed stay.</p>}
          confirm="Withdraw"
          onCancel={() => setRevoking(null)}
          onConfirm={() => {
            const { round, reader } = revoking;
            setRevoking(null);
            void links
              .revoke(round.id, reader.reviewID)
              .then(() => announce(`${reader.name}’s link was withdrawn.`))
              .then(refresh)
              .catch((e) => setStatusError(e instanceof Error ? e.message : String(e)));
          }}
        />
      ) : null}
    </div>
  );
}

function NewRoundForm({
  checkpoints,
  links,
  onDone,
}: {
  checkpoints: { id: string; name: string; createdAt: Date }[];
  links: ReaderLinks;
  onDone: (round: WebReaderRound | null) => void | Promise<void>;
}) {
  const [name, setName] = useState('First readers');
  const [checkpointId, setCheckpointId] = useState('');
  const [readers, setReaders] = useState('');
  const [returnBy, setReturnBy] = useState('');
  const [expires, setExpires] = useState('');
  const [message, setMessage] = useState('Please don’t share');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => first.current?.focus(), []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const names = readers.split(/\n|,/).map((s) => s.trim()).filter(Boolean);
    if (!names.length) return setError('Add at least one reader.');
    setBusy('Starting…');
    try {
      const { round } = await links.createRound(
        { name, checkpointId: checkpointId || null, readers: names, returnBy: fromDateInput(returnBy), message, expiresAt: fromDateInput(expires), from: authorName() ?? 'The writer' },
        (t) => setBusy(t || null),
      );
      await onDone(round);
    } catch (err) {
      setBusy(null);
      setError(errorText(err, 'readerLinks'));
    }
  };

  return (
    <form className="new-round" onSubmit={(e) => void submit(e)} aria-labelledby="new-round-title">
      <h2 id="new-round-title">New round</h2>
      <div className="field">
        <label htmlFor="round-name">Name</label>
        <input id="round-name" ref={first} value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
      </div>
      <div className="field">
        <label htmlFor="round-checkpoint">Checkpoint</label>
        <select id="round-checkpoint" value={checkpointId} onChange={(e) => setCheckpointId(e.target.value)}>
          <option value="">Make one now (named after the round)</option>
          {checkpoints.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name || 'Untitled'} — {DAY.format(c.createdAt)}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor="round-readers">Readers</label>
        <textarea id="round-readers" rows={4} value={readers} onChange={(e) => setReaders(e.target.value)} aria-describedby="round-readers-help" />
        <p className="help" id="round-readers-help">
          One name per line. Each reader gets their own link, marked with their name.
        </p>
      </div>
      <div className="round-dates">
        <div className="field">
          <label htmlFor="round-return">Notes by</label>
          <input id="round-return" type="date" value={returnBy} onChange={(e) => setReturnBy(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="round-expires">Links stop working on</label>
          <input id="round-expires" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} aria-describedby="round-expires-help" />
          <p className="help" id="round-expires-help">
            Optional.
          </p>
        </div>
      </div>
      <div className="field">
        <label htmlFor="round-message">Message</label>
        <input id="round-message" value={message} onChange={(e) => setMessage(e.target.value)} maxLength={500} />
      </div>
      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}
      {busy ? (
        <p className="status-note" role="status">
          {busy}
        </p>
      ) : null}
      <div className="actions">
        <button type="button" className="button secondary" onClick={() => void onDone(null)} disabled={!!busy}>
          Cancel
        </button>
        <button type="submit" className="button primary" disabled={!!busy}>
          Make links
        </button>
      </div>
    </form>
  );
}

function RoundView({
  round,
  checkpointName,
  readOrigin,
  status,
  progress,
  canWrite,
  onRevoke,
}: {
  round: WebReaderRound;
  checkpointName: string | null;
  readOrigin: string | null;
  status: Map<string, LinkStatus> | null;
  progress: Map<string, number>;
  canWrite: boolean;
  onRevoke: (reader: WebReader) => void;
}) {
  const [qr, setQr] = useState<string | null>(null);
  const sub = [checkpointName ? `“${checkpointName}”` : 'A checkpoint not on this computer', `made ${DAY.format(round.createdAt)}`, round.returnBy ? `notes by ${DAY.format(round.returnBy)}` : '']
    .filter(Boolean)
    .join(' · ');
  return (
    <section className="round" aria-labelledby={`round-${round.id}`} data-round={round.id}>
      <h2 id={`round-${round.id}`}>{round.name}</h2>
      <p className="round-sub">{sub}</p>
      <ul className="round-readers">
        {round.readers.map((reader) => {
          const s = status?.get(reader.reviewID) ?? null;
          const link = readOrigin ? ReaderLinks.link(readOrigin, reader) : null;
          const withdrawn = !!reader.revokedAt || !!s?.revokedAt;
          const expired = !withdrawn && ((s?.expiresAt ?? reader.expiresAt?.getTime() ?? Infinity) <= Date.now());
          const fraction = progress.get(reader.reviewID);
          const state = withdrawn ? 'Withdrawn' : expired ? 'Expired' : s?.openedAt ? `Opened ${WHEN.format(new Date(s.lastOpenedAt ?? s.openedAt))}` : s ? 'Not opened yet' : '';
          const notes = s?.notes ?? null;
          return (
            <li key={reader.id} className="round-reader" data-reader={reader.name} data-review-id={reader.reviewID} data-state={withdrawn ? 'withdrawn' : expired ? 'expired' : s?.openedAt ? 'opened' : 'waiting'}>
              <div className="reader-line">
                <span className="reader-name">{reader.name}</span>
                <span className="reader-state">{state}</span>
                {fraction !== undefined && !withdrawn ? (
                  <span className="reader-progress" data-progress={Math.round(fraction * 100)}>
                    <span className="meter" aria-hidden="true">
                      <span className="meter-fill" style={{ width: `${Math.round(fraction * 100)}%` }} />
                    </span>
                    {Math.round(fraction * 100)}% read
                  </span>
                ) : null}
                {notes !== null ? (
                  <span className="reader-notes" data-notes={notes}>
                    {notes === 1 ? '1 note' : `${notes} notes`}
                  </span>
                ) : null}
              </div>
              {!withdrawn && link ? (
                <div className="reader-link">
                  <input className="link-field" readOnly value={link} aria-label={`${reader.name}’s link`} onFocus={(e) => e.currentTarget.select()} />
                  <button
                    type="button"
                    className="button secondary"
                    onClick={() =>
                      void navigator.clipboard
                        .writeText(link)
                        .then(() => announce(`${reader.name}’s link copied.`))
                        .catch(() => announce('Copying isn’t allowed here: select the link and copy it.'))
                    }
                  >
                    Copy link
                  </button>
                  <button type="button" className="button secondary" aria-expanded={qr === reader.id} onClick={() => setQr(qr === reader.id ? null : reader.id)}>
                    QR code
                  </button>
                  <button type="button" className="button secondary danger" disabled={!canWrite} onClick={() => onRevoke(reader)}>
                    Withdraw
                  </button>
                </div>
              ) : null}
              {qr === reader.id && link && !withdrawn ? <QrCode text={link} label={`QR code for ${reader.name}’s link`} /> : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** A QR code drawn as SVG in the page (qrcode-generator, bundled): no image service ever sees the link. */
export function QrCode({ text, label }: { text: string; label: string }) {
  const { n, d } = useMemo(() => {
    const q = qrcode(0, 'M');
    q.addData(text);
    q.make();
    const count = q.getModuleCount();
    let path = '';
    for (let r = 0; r < count; r++) for (let c = 0; c < count; c++) if (q.isDark(r, c)) path += `M${c + 4} ${r + 4}h1v1h-1z`;
    return { n: count + 8, d: path };
  }, [text]);
  return (
    <svg className="qr" viewBox={`0 0 ${n} ${n}`} role="img" aria-label={label} shapeRendering="crispEdges">
      <rect width={n} height={n} className="qr-ground" />
      <path d={d} className="qr-ink" />
    </svg>
  );
}
